import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, Pencil, Loader2, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

// Jotform-style native form builder. Forms live in custom_forms; patients
// fill them from the client portal and answers land in custom_form_responses.

const FIELD_TYPES = [
  { key: 'text',     label: 'Short answer' },
  { key: 'textarea', label: 'Long answer' },
  { key: 'radio',    label: 'Multiple choice (pick one)' },
  { key: 'checkbox', label: 'Checkboxes (pick many)' },
  { key: 'select',   label: 'Dropdown' },
  { key: 'yesno',    label: 'Yes / No' },
  { key: 'date',     label: 'Date' },
  { key: 'email',    label: 'Email' },
  { key: 'phone',    label: 'Phone' },
  { key: 'number',   label: 'Number' },
];

const HAS_OPTIONS = new Set(['radio', 'checkbox', 'select']);

const newField = () => ({
  id: `f_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
  type: 'text',
  label: '',
  required: false,
  options: [],
});

const inputClass = 'w-full px-3.5 py-[10px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] focus:outline-none focus:border-[#0DA7CA] transition-all';

export default function FormBuilderTab() {
  const { currentOrg } = useOrg();
  const [forms, setForms] = useState(null);
  const [editor, setEditor] = useState(null);      // {id?, title, description, fields, is_active}
  const [responsesFor, setResponsesFor] = useState(null); // form object
  const [responses, setResponses] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!currentOrg) return;
    api.listCustomForms(currentOrg.id).then(setForms).catch(() => setForms([]));
  }, [currentOrg]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!responsesFor || !currentOrg) { setResponses(null); return; }
    api.getCustomFormResponses(currentOrg.id, responsesFor.id)
      .then(setResponses)
      .catch(() => setResponses([]));
  }, [responsesFor, currentOrg]);

  const openNew = () => setEditor({
    title: '', description: '', is_active: true,
    fields: [{ ...newField(), label: 'Full name', required: true }],
  });
  const openEdit = (f) => setEditor({ id: f.id, title: f.title, description: f.description || '', is_active: f.is_active, fields: f.fields || [] });

  const setField = (i, patch) => setEditor(ed => ({
    ...ed,
    fields: ed.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f),
  }));
  const moveField = (i, dir) => setEditor(ed => {
    const fields = [...ed.fields];
    const j = i + dir;
    if (j < 0 || j >= fields.length) return ed;
    [fields[i], fields[j]] = [fields[j], fields[i]];
    return { ...ed, fields };
  });
  const removeField = (i) => setEditor(ed => ({ ...ed, fields: ed.fields.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!editor.title.trim()) { setError('Give the form a title'); return; }
    if (!editor.fields.length) { setError('Add at least one question'); return; }
    if (editor.fields.some(f => !f.label.trim())) { setError('Every question needs a label'); return; }
    setSaving(true); setError('');
    const payload = {
      title: editor.title,
      description: editor.description,
      is_active: editor.is_active,
      fields: editor.fields.map(f => ({ ...f, options: HAS_OPTIONS.has(f.type) ? f.options : [] })),
    };
    try {
      if (editor.id) await api.updateCustomForm(currentOrg.id, editor.id, payload);
      else await api.createCustomForm(currentOrg.id, payload);
      setEditor(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const destroy = async (f) => {
    if (!window.confirm(`Delete "${f.title}"? Patients will no longer see it.`)) return;
    try { await api.deleteCustomForm(currentOrg.id, f.id); load(); } catch { /* keep list */ }
  };

  if (forms === null) {
    return <div className="py-16 flex justify-center"><Loader2 size={22} className="animate-spin text-muted-c" /></div>;
  }

  // ── Editor view ──────────────────────────────────────────────────
  if (editor) {
    return (
      <div className="max-w-[680px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-bold text-primary-c">{editor.id ? 'Edit form' : 'New form'}</h2>
          <button onClick={() => setEditor(null)} className="text-[12px] text-muted-c hover:text-primary-c transition-colors">&larr; Back to forms</button>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-5 mb-4 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Form title</label>
            <input className={inputClass} placeholder="e.g. New Patient Health Questionnaire" value={editor.title} onChange={e => setEditor(ed => ({ ...ed, title: e.target.value }))} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-1.5">Description (patients see this)</label>
            <textarea className={inputClass} rows={2} placeholder="A few sentences about why you're asking…" value={editor.description} onChange={e => setEditor(ed => ({ ...ed, description: e.target.value }))} />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-secondary-c cursor-pointer w-fit">
            <input type="checkbox" checked={editor.is_active} onChange={e => setEditor(ed => ({ ...ed, is_active: e.target.checked }))} />
            Visible to patients
          </label>
        </div>

        <div className="space-y-3 mb-4">
          {editor.fields.map((f, i) => (
            <div key={f.id} className="bg-elevated border border-theme rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-2.5 mb-2.5">
                <input
                  className={inputClass}
                  placeholder={`Question ${i + 1}`}
                  value={f.label}
                  onChange={e => setField(i, { label: e.target.value })}
                />
                <select
                  className={`${inputClass} sm:w-[210px] flex-shrink-0`}
                  value={f.type}
                  onChange={e => setField(i, { type: e.target.value })}
                >
                  {FIELD_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              {HAS_OPTIONS.has(f.type) && (
                <textarea
                  className={`${inputClass} mb-2.5`}
                  rows={2}
                  placeholder={'One option per line, e.g.\nAuto accident\nSlip and fall'}
                  value={(f.options || []).join('\n')}
                  onChange={e => setField(i, { options: e.target.value.split('\n') })}
                  onBlur={e => setField(i, { options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                />
              )}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-[12px] text-secondary-c cursor-pointer">
                  <input type="checkbox" checked={f.required} onChange={e => setField(i, { required: e.target.checked })} />
                  Required
                </label>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveField(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg text-muted-c hover:text-primary-c hover:bg-tertiary disabled:opacity-30 transition-all"><ChevronUp size={15} /></button>
                  <button onClick={() => moveField(i, 1)} disabled={i === editor.fields.length - 1} className="p-1.5 rounded-lg text-muted-c hover:text-primary-c hover:bg-tertiary disabled:opacity-30 transition-all"><ChevronDown size={15} /></button>
                  <button onClick={() => removeField(i)} className="p-1.5 rounded-lg text-danger hover:bg-danger-soft transition-all"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setEditor(ed => ({ ...ed, fields: [...ed.fields, newField()] }))}
          className="w-full py-3 rounded-xl border border-dashed border-strong text-[13px] font-semibold text-muted-c hover:text-primary-c hover:border-[#0DA7CA] transition-all mb-5 flex items-center justify-center gap-2"
        >
          <Plus size={15} /> Add question
        </button>

        {error && <div className="px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium mb-4">{error}</div>}

        <div className="flex justify-end gap-2.5">
          <button onClick={() => setEditor(null)} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50">
            {saving ? 'Saving…' : editor.id ? 'Save changes' : 'Create form'}
          </button>
        </div>
      </div>
    );
  }

  // ── Responses view ───────────────────────────────────────────────
  if (responsesFor) {
    const fieldLabel = Object.fromEntries((responsesFor.fields || []).map(f => [f.id, f.label]));
    return (
      <div className="max-w-[680px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-bold text-primary-c">{responsesFor.title} — responses</h2>
          <button onClick={() => setResponsesFor(null)} className="text-[12px] text-muted-c hover:text-primary-c transition-colors">&larr; Back to forms</button>
        </div>
        {responses === null ? (
          <div className="py-16 flex justify-center"><Loader2 size={22} className="animate-spin text-muted-c" /></div>
        ) : !responses.length ? (
          <div className="bg-tertiary border border-theme rounded-xl p-8 text-center text-[13px] text-muted-c">No responses yet.</div>
        ) : (
          <div className="space-y-3">
            {responses.map(r => (
              <div key={r.id} className="bg-elevated border border-theme rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[13px] font-semibold text-primary-c">
                    {r.client ? `${r.client.first_name} ${r.client.last_name}` : 'Patient'}
                    <span className="text-muted-c font-normal"> · {r.client?.email}</span>
                  </div>
                  <div className="text-[11px] text-muted-c">{format(new Date(r.submitted_at), 'MMM d, yyyy h:mm a')}</div>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(r.answers || {}).map(([k, v]) => (
                    <div key={k} className="text-[12.5px]">
                      <span className="text-muted-c">{fieldLabel[k] || k}: </span>
                      <span className="text-primary-c font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────
  return (
    <div className="max-w-[680px]">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[12px] text-muted-c">
          Build your own forms — patients complete them in their portal, answers collect here.
        </p>
        <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all flex-shrink-0">
          <Plus size={15} /> New form
        </button>
      </div>

      {!forms.length ? (
        <div className="bg-tertiary border border-theme rounded-xl p-10 text-center">
          <ClipboardList size={30} className="mx-auto mb-3 text-muted-c opacity-40" />
          <div className="text-[14px] font-semibold text-primary-c mb-1">No forms yet</div>
          <p className="text-[12px] text-muted-c">Create a questionnaire, screening form or survey — no PDF needed.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {forms.map(f => (
            <div key={f.id} className="bg-elevated border border-theme rounded-xl p-4 flex items-center gap-3.5">
              <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 ${f.is_active ? 'bg-accent-glow' : 'bg-tertiary'}`}>
                <ClipboardList size={17} className={f.is_active ? 'text-accent-light' : 'text-muted-c'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-primary-c truncate">{f.title}</div>
                <div className="text-[11.5px] text-muted-c mt-0.5">
                  {(f.fields || []).length} question{(f.fields || []).length !== 1 ? 's' : ''}
                  {' · '}{f.response_count} response{f.response_count !== 1 ? 's' : ''}
                  {!f.is_active && ' · Hidden from patients'}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => setResponsesFor(f)} title="Responses" className="p-2 rounded-lg text-muted-c hover:text-primary-c hover:bg-tertiary transition-all"><Eye size={15} /></button>
                <button onClick={() => openEdit(f)} title="Edit" className="p-2 rounded-lg text-muted-c hover:text-primary-c hover:bg-tertiary transition-all"><Pencil size={15} /></button>
                <button onClick={() => destroy(f)} title="Delete" className="p-2 rounded-lg text-danger hover:bg-danger-soft transition-all"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
